// src/auth/error-codes.ts
var AuthErrorCode = {
  USER_ARCHIVED: "USER_ARCHIVED",
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
  INVALID_REFRESH_TOKEN: "INVALID_REFRESH_TOKEN",
  SESSION_REVOKED: "SESSION_REVOKED"
};

// src/user/user.ts
import * as zod from "zod";
var z2 = zod.z ?? zod;
var UserRoleSchema = z2.enum(["WORKER", "MANAGER", "DIRECTOR", "SUPERUSER"]);
var Role = UserRoleSchema.enum;
var UserRolesSchema = z2.array(UserRoleSchema).nonempty("Mindestens eine Rolle ausw\xE4hlen");
var ROLE_RANK = {
  WORKER: 0,
  MANAGER: 1,
  DIRECTOR: 2,
  SUPERUSER: 3
};
var hasRole = (roles, r) => roles.includes(r);
var hasAnyRole = (roles, rs) => roles.some((r) => rs.includes(r));
var hasOnlyRole = (roles, r) => roles.length === 1 && roles[0] === r;
var highestRole = (roles) => roles.reduce((acc, r) => ROLE_RANK[r] > ROLE_RANK[acc] ? r : acc, "WORKER");
var TechnicianProfileDataSchema = z2.object({
  workingDays: z2.array(z2.number().int().min(1).max(7)),
  shiftStart: z2.string(),
  shiftEnd: z2.string(),
  breakStart: z2.string(),
  trades: z2.array(z2.number().int().nonnegative())
});
var CreateUserDtoSchema = z2.object({
  email: z2.string().email(),
  name: z2.string(),
  roles: UserRolesSchema,
  technicianProfile: TechnicianProfileDataSchema.optional(),
  password: z2.string().min(8).optional()
}).superRefine((d, ctx) => {
  if (d.roles.includes("WORKER") && !d.technicianProfile) {
    ctx.addIssue({ code: z2.ZodIssueCode.custom, message: "Techniker-Profil ist erforderlich f\xFCr Handwerker", path: ["technicianProfile"] });
  }
  if (!d.roles.includes("WORKER") && d.technicianProfile) {
    ctx.addIssue({ code: z2.ZodIssueCode.custom, message: "Techniker-Profil nur f\xFCr Handwerker erlaubt", path: ["technicianProfile"] });
  }
});
var UpdateUserDtoSchema = z2.object({
  email: z2.string().email().optional(),
  name: z2.string().optional(),
  roles: UserRolesSchema.optional(),
  technicianProfile: TechnicianProfileDataSchema.optional()
});
var UserStatusFilterSchema = z2.enum(["active", "archived", "all"]);
var UserFiltersSchema = z2.preprocess(
  (raw) => {
    if (raw !== null && typeof raw === "object" && !Array.isArray(raw)) {
      const { role, roles, ...rest } = raw;
      const normalizedRoles = roles !== void 0 ? Array.isArray(roles) ? roles : [roles] : role !== void 0 ? [role] : void 0;
      return normalizedRoles !== void 0 ? { ...rest, roles: normalizedRoles } : rest;
    }
    return raw;
  },
  z2.object({
    page: z2.coerce.number().int().positive().optional(),
    limit: z2.coerce.number().int().positive().max(500).optional(),
    search: z2.string().optional(),
    roles: z2.array(UserRoleSchema).optional(),
    status: UserStatusFilterSchema.optional(),
    tradeId: z2.coerce.number().int().nonnegative().optional()
  })
);

// src/audit-log/audit-log.ts
import * as zod2 from "zod";
var z4 = zod2.z ?? zod2;
var AuditLogEntityTypeSchema = z4.enum(["WORK_ORDER", "APPOINTMENT", "TASK"]);
var AuditLogActionSchema = z4.enum([
  "WORK_ORDER_CREATED",
  "WORK_ORDER_STATUS_CHANGED",
  "WORK_ORDER_COMPLETED",
  "APPOINTMENT_CREATED",
  "APPOINTMENT_UPDATED",
  "APPOINTMENT_RESCHEDULED",
  "APPOINTMENT_TECHNICIAN_CHANGED",
  "APPOINTMENT_STARTED",
  "APPOINTMENT_COMPLETED",
  "APPOINTMENT_FOLLOW_UP_REQUESTED",
  "APPOINTMENT_EXPIRED",
  "TASK_RESCHEDULED"
]);
var AuditLogActorSchema = z4.object({
  id: z4.string(),
  name: z4.string(),
  roles: z4.array(UserRoleSchema)
}).nullable();
var AuditLogSchema = z4.object({
  id: z4.number().int(),
  entityType: AuditLogEntityTypeSchema,
  entityId: z4.string(),
  workOrderId: z4.string().nullable(),
  action: z4.string(),
  description: z4.string(),
  payload: z4.unknown().nullable().optional(),
  actorUserId: z4.number().int().nullable(),
  actorRoles: z4.array(UserRoleSchema),
  actor: AuditLogActorSchema,
  createdAt: z4.string()
});
var AuditLogFiltersSchema = z4.object({
  workOrderId: z4.string()
});

// src/work-order/work-order.ts
import * as zod3 from "zod";
var z6 = zod3.z ?? zod3;
var WorkOrderMieterSchema = z6.object({
  name: z6.string().nullable(),
  telefon: z6.string().nullable(),
  email: z6.string().nullable()
});
var WorkOrderExternalSnapshotSchema = z6.object({
  auftragJahr: z6.number().int().positive(),
  auftragNr: z6.number().int().positive(),
  auftragsdatum: z6.string().nullable(),
  ausfuehrungBis: z6.string().nullable(),
  fertigmeldungsdat: z6.string().nullable(),
  auftStatus: z6.number().int().nullable(),
  ve: z6.number().int().nullable(),
  wohnung: z6.number().int().nullable(),
  veAdresse: z6.string().nullable(),
  hauseingang: z6.string().nullable(),
  gebaeude: z6.string().nullable(),
  mieter: WorkOrderMieterSchema,
  handwerkerName: z6.string().nullable(),
  gewerkId: z6.number().int().nullable(),
  gewerkeText: z6.string().nullable(),
  leistungText: z6.string().nullable(),
  infoText: z6.string().nullable(),
  individuellerText: z6.string().nullable(),
  erfasserName: z6.string().nullable(),
  isWohnungsmodernisierung: z6.boolean()
});
var WorkOrderLocalDataSchema = z6.object({
  updatedAt: z6.string().nullable()
});
var WorkOrderStatusSchema = z6.enum([
  "NOT_PLANNED",
  "PLANNED",
  "FOLLOW_UP_REQUIRED_1",
  "FOLLOW_UP_PLANNED_1",
  "FOLLOW_UP_REQUIRED_2",
  "FOLLOW_UP_PLANNED_2",
  "COMPLETED",
  "COMPLETED_FOLLOW_UP_1",
  "COMPLETED_FOLLOW_UP_2"
]);
var WorkOrderSchema = z6.object({
  workOrderId: z6.string().nullable(),
  status: WorkOrderStatusSchema,
  external: WorkOrderExternalSnapshotSchema
});
var WorkOrderSortFieldSchema = z6.enum([
  "workOrderId",
  "mieter",
  "handwerker",
  "datum",
  "ve",
  "hauseingang",
  "wohnung"
]);
var WorkOrderSortDirectionSchema = z6.enum(["ASC", "DESC"]);
var WorkOrderFiltersSchema = z6.object({
  page: z6.coerce.number().int().positive().optional(),
  limit: z6.coerce.number().int().positive().max(500).optional(),
  workOrderId: z6.string().optional(),
  mieter: z6.string().optional(),
  handwerker: z6.string().optional(),
  datum: z6.string().optional(),
  datumFrom: z6.string().optional(),
  datumTo: z6.string().optional(),
  wohnung: z6.coerce.number().int().optional(),
  hauseingang: z6.string().optional(),
  ve: z6.coerce.number().int().optional(),
  veAdresse: z6.string().optional(),
  sortField: WorkOrderSortFieldSchema.optional(),
  sortDirection: WorkOrderSortDirectionSchema.optional(),
  local: z6.preprocess((v) => v === "false" ? false : v === "true" ? true : v, z6.boolean().optional()),
  status: WorkOrderStatusSchema.optional(),
  gewerkId: z6.coerce.number().int().optional()
});
var CreateWorkOrderDtoSchema = z6.object({
  workOrderId: z6.string()
});

// src/work-order/work-order-detail.ts
import { z as z9 } from "zod";

// src/appointment/appointment.ts
import { z as z8 } from "zod";

// src/time-entry/time-entry.ts
import { z as z7 } from "zod";
var TimeEntryTypeSchema = z7.enum(["BREAK", "TIME_MANAGEMENT"]);
var TimeEntryCategorySchema = z7.enum([
  "SICK_LEAVE",
  "VACATION",
  "TRAINING",
  "PERSONAL_LEAVE"
]);
var TimeEntryTechnicianSchema = z7.object({
  id: z7.number().int(),
  fullName: z7.string()
});
var TimeEntryConflictItemSchema = z7.object({
  type: z7.enum(["appointment", "time_management", "task"]),
  id: z7.number().int(),
  technicianId: z7.number().int(),
  technicianName: z7.string(),
  startTime: z7.string().datetime(),
  endTime: z7.string().datetime(),
  label: z7.string()
});
var TimeEntrySchema = z7.object({
  id: z7.number().int(),
  assignedTechnicianId: z7.number().int().nullable(),
  entryType: TimeEntryTypeSchema,
  category: TimeEntryCategorySchema.nullable().optional(),
  comment: z7.string().nullable().optional(),
  technicians: z7.array(TimeEntryTechnicianSchema).optional(),
  needsRescheduling: z7.boolean().optional(),
  reschedulingReason: TimeEntryCategorySchema.nullable().optional(),
  reschedulingRequestedAt: z7.string().nullable().optional(),
  startTime: z7.string().datetime(),
  endTime: z7.string().datetime(),
  createdAt: z7.string().datetime()
});
var UpsertBreakDtoSchema = z7.object({
  assignedTechnicianId: z7.coerce.number().int().positive(),
  date: z7.string(),
  startTime: z7.string().datetime()
});
var TimeManagementListItemSchema = z7.object({
  id: z7.number().int(),
  technicians: z7.array(TimeEntryTechnicianSchema),
  category: TimeEntryCategorySchema,
  comment: z7.string().nullable().optional(),
  needsRescheduling: z7.boolean().optional(),
  reschedulingReason: TimeEntryCategorySchema.nullable().optional(),
  reschedulingRequestedAt: z7.string().nullable().optional(),
  startTime: z7.string().datetime(),
  endTime: z7.string().datetime(),
  createdAt: z7.string().datetime()
});
var TimeManagementSortFieldSchema = z7.enum([
  "category",
  "startTime",
  "endTime"
]);
var TimeManagementSortDirectionSchema = z7.enum(["ASC", "DESC"]);
var TimeManagementFiltersSchema = z7.object({
  page: z7.coerce.number().int().positive().optional(),
  limit: z7.coerce.number().int().positive().max(500).optional(),
  technicianName: z7.string().optional(),
  category: TimeEntryCategorySchema.optional(),
  dateFrom: z7.string().optional(),
  dateTo: z7.string().optional(),
  sortField: TimeManagementSortFieldSchema.optional(),
  sortDirection: TimeManagementSortDirectionSchema.optional()
});
var CreateTimeManagementDtoSchema = z7.object({
  assignedTechnicianIds: z7.array(z7.coerce.number().int().positive()).min(1),
  category: TimeEntryCategorySchema,
  comment: z7.string().optional(),
  force: z7.boolean().optional(),
  startTime: z7.string().datetime(),
  endTime: z7.string().datetime()
});
var UpdateTimeManagementDtoSchema = z7.object({
  assignedTechnicianIds: z7.array(z7.coerce.number().int().positive()).min(1).optional(),
  category: TimeEntryCategorySchema.optional(),
  comment: z7.string().nullable().optional(),
  force: z7.boolean().optional(),
  startTime: z7.string().datetime().optional(),
  endTime: z7.string().datetime().optional()
});

// src/appointment/appointment.ts
var AppointmentStatusSchema = z8.enum([
  "PLANNED",
  "FOLLOW_UP",
  "COMPLETED",
  "COMPLETED_WITH_FOLLOW_UP",
  "EXPIRED"
]);
var AppointmentTechnicianSchema = z8.object({
  id: z8.string(),
  name: z8.string().nullable(),
  avatar: z8.string().nullable()
});
var AppointmentSchema = z8.object({
  id: z8.string(),
  workOrderId: z8.string(),
  assignedTechnicianId: z8.string(),
  tradeCode: z8.number().int(),
  plannedStart: z8.string(),
  sequenceNumber: z8.number().int().min(1),
  plannedDepartureDuration: z8.number().int().min(0),
  plannedTravelDuration: z8.number().int().min(0),
  plannedWorkDuration: z8.number().positive(),
  actualWorkDuration: z8.number().positive().nullable(),
  actualTravelDuration: z8.number().int().min(0).nullable(),
  actualDepartureDuration: z8.number().int().min(0).nullable(),
  attachments: z8.array(z8.string()).nullable(),
  isFullDay: z8.boolean().optional(),
  status: AppointmentStatusSchema,
  needsRescheduling: z8.boolean().optional(),
  reschedulingReason: TimeEntryCategorySchema.nullable().optional(),
  reschedulingRequestedAt: z8.string().nullable().optional(),
  followUpReason: z8.string().nullable().optional(),
  description: z8.string().nullable().optional(),
  technicianComment: z8.string().nullable().optional(),
  actualStart: z8.string().nullable(),
  actualEnd: z8.string().nullable(),
  createdAt: z8.string(),
  updatedAt: z8.string(),
  technician: AppointmentTechnicianSchema.nullable(),
  workOrder: WorkOrderSchema.nullable()
});
var CreateAppointmentDtoSchema = z8.object({
  workOrderId: z8.string(),
  assignedTechnicianId: z8.coerce.number().int().positive(),
  plannedStart: z8.string().datetime(),
  tradeCode: z8.number().int().min(0),
  plannedDepartureDuration: z8.number().int().min(0),
  plannedTravelDuration: z8.number().int().min(0),
  plannedWorkDuration: z8.number().positive(),
  attachments: z8.array(z8.string()).nullable(),
  description: z8.string().nullable().optional(),
  isFullDay: z8.boolean().optional()
});
var UpdateAppointmentDtoSchema = z8.object({
  assignedTechnicianId: z8.coerce.number().int().positive(),
  plannedStart: z8.string().datetime(),
  tradeCode: z8.number().int().min(0),
  plannedDepartureDuration: z8.number().int().min(0),
  plannedTravelDuration: z8.number().int().min(0),
  plannedWorkDuration: z8.number().positive(),
  attachments: z8.array(z8.string()).nullable(),
  followUpReason: z8.string().nullable(),
  actualStart: z8.string().datetime(),
  actualEnd: z8.string().datetime(),
  actualWorkDuration: z8.number().int().min(0),
  actualTravelDuration: z8.number().int().min(0),
  actualDepartureDuration: z8.number().int().min(0),
  description: z8.string().nullable(),
  isFullDay: z8.boolean()
}).partial();
var StartAppointmentDtoSchema = z8.object({
  actualTravelDuration: z8.number().int().min(0).multipleOf(15)
});
var CompleteAppointmentDtoSchema = z8.object({
  actualWorkDuration: z8.number().int().min(0).multipleOf(15),
  actualDepartureDuration: z8.number().int().min(0).multipleOf(15).nullable(),
  endTime: z8.string().regex(/^\d{2}:\d{2}$/),
  technicianComment: z8.string().optional()
});
var FollowUpAppointmentDtoSchema = z8.object({
  followUpReason: z8.string().min(1),
  actualWorkDuration: z8.number().int().min(0).multipleOf(15),
  actualDepartureDuration: z8.number().int().min(0).multipleOf(15).nullable(),
  endTime: z8.string().regex(/^\d{2}:\d{2}$/),
  technicianComment: z8.string().optional()
});
var TechnicianAppointmentsQuerySchema = z8.object({
  from: z8.string().datetime(),
  to: z8.string().datetime()
});
var TechnicianAppointmentsItemSchema = z8.object({
  technicianId: z8.string(),
  technicianName: z8.string().nullable(),
  technicianRoles: z8.array(UserRoleSchema),
  technicianAvatar: z8.string().nullable(),
  defaultBreakStart: z8.string().datetime().nullable(),
  workingDays: z8.array(z8.number().int().min(1).max(7)),
  shiftStart: z8.string().datetime().nullable(),
  shiftEnd: z8.string().datetime().nullable(),
  appointments: z8.array(AppointmentSchema),
  breakOverrides: z8.array(TimeEntrySchema),
  timeManagementEntries: z8.array(TimeManagementListItemSchema)
});
var TechnicianAppointmentsResultSchema = z8.object({
  from: z8.string().datetime(),
  to: z8.string().datetime(),
  technicians: z8.array(TechnicianAppointmentsItemSchema)
});

// src/work-order/work-order-detail.ts
var WorkOrderWithAppointmentsSchema = WorkOrderSchema.extend({
  appointments: z9.array(AppointmentSchema)
});

// src/technician-profile/technician-profile.ts
import * as zod4 from "zod";
var z11 = zod4.z ?? zod4;
var TechnicianProfileSchema = z11.object({
  id: z11.number().int(),
  userId: z11.number().int(),
  workingDays: z11.array(z11.number().int().min(1).max(7)),
  shiftStart: z11.string().datetime(),
  shiftEnd: z11.string().datetime(),
  breakStart: z11.string().datetime(),
  trades: z11.array(z11.number().int().nonnegative())
});
var CreateTechnicianProfileDtoSchema = z11.object({
  userId: z11.number().int(),
  workingDays: z11.array(z11.number().int().min(1).max(7)),
  shiftStart: z11.string().datetime(),
  shiftEnd: z11.string().datetime(),
  breakStart: z11.string().datetime(),
  trades: z11.array(z11.number().int().nonnegative())
});

// src/gewerk/gewerk.ts
import * as zod5 from "zod";
var z13 = zod5.z ?? zod5;
var GewerkSchema = z13.object({
  id: z13.number().int(),
  text: z13.string()
});
var GewerkFiltersSchema = z13.object({
  page: z13.coerce.number().int().min(1).optional(),
  limit: z13.coerce.number().int().min(1).max(500).optional(),
  search: z13.string().optional()
});

// src/task/task.ts
import { z as z14 } from "zod";
var TaskStatusSchema = z14.enum([
  "PLANNED",
  "COMPLETED",
  "DELAYED",
  "CANCELLED"
]);
var TaskTypeSchema = z14.enum(["ERRANDS", "GENERAL_TASK"]);
var TaskTechnicianSchema = z14.object({
  id: z14.number().int(),
  fullName: z14.string()
});
var TaskListItemSchema = z14.object({
  id: z14.number().int(),
  title: z14.string(),
  taskType: TaskTypeSchema,
  status: TaskStatusSchema,
  plannedStart: z14.string().datetime().nullable(),
  deadline: z14.string().datetime().nullable().optional(),
  assignedTechnician: TaskTechnicianSchema.nullable(),
  plannedWorkDuration: z14.number(),
  actualWorkDuration: z14.number().nullable().optional(),
  plannedTravelDuration: z14.number(),
  actualTravelDuration: z14.number().nullable().optional(),
  plannedDepartureDuration: z14.number(),
  actualDepartureDuration: z14.number().nullable().optional(),
  needsRescheduling: z14.boolean().optional(),
  reschedulingReason: TimeEntryCategorySchema.nullable().optional(),
  reschedulingRequestedAt: z14.string().nullable().optional(),
  actualStart: z14.string().nullable().optional(),
  actualEnd: z14.string().nullable().optional()
});
var TaskSchema = z14.object({
  id: z14.number().int(),
  title: z14.string(),
  description: z14.string().nullable().optional(),
  taskType: TaskTypeSchema,
  status: TaskStatusSchema,
  plannedStart: z14.string().datetime().nullable(),
  deadline: z14.string().datetime().nullable().optional(),
  assignedTechnicianId: z14.number().int().nullable(),
  assignedTechnician: TaskTechnicianSchema.nullable(),
  createdByUserId: z14.number().int(),
  plannedWorkDuration: z14.number(),
  actualWorkDuration: z14.number().nullable().optional(),
  plannedTravelDuration: z14.number(),
  actualTravelDuration: z14.number().nullable().optional(),
  plannedDepartureDuration: z14.number(),
  actualDepartureDuration: z14.number().nullable().optional(),
  attachments: z14.array(z14.string()),
  actualStart: z14.string().nullable().optional(),
  actualEnd: z14.string().nullable().optional(),
  createdAt: z14.string().datetime()
});
var CreateTaskDtoSchema = z14.object({
  title: z14.string().min(1),
  description: z14.string().optional(),
  assignedTechnicianId: z14.coerce.number().int().positive().nullable().optional(),
  taskType: TaskTypeSchema,
  plannedStart: z14.string().datetime().nullable().optional(),
  deadline: z14.string().datetime().optional(),
  plannedDepartureDuration: z14.number().min(0),
  plannedTravelDuration: z14.number().min(0),
  plannedWorkDuration: z14.number().min(0),
  attachments: z14.array(z14.string()).optional()
});
var UpdateTaskDtoSchema = z14.object({
  title: z14.string().min(1).optional(),
  description: z14.string().nullable().optional(),
  assignedTechnicianId: z14.coerce.number().int().positive().optional(),
  taskType: TaskTypeSchema.optional(),
  status: TaskStatusSchema.optional(),
  plannedStart: z14.string().datetime().optional(),
  deadline: z14.string().datetime().nullable().optional(),
  plannedDepartureDuration: z14.number().min(0).optional(),
  actualDepartureDuration: z14.number().min(0).nullable().optional(),
  plannedTravelDuration: z14.number().min(0).optional(),
  actualTravelDuration: z14.number().min(0).nullable().optional(),
  plannedWorkDuration: z14.number().min(0).optional(),
  actualWorkDuration: z14.number().min(0).optional(),
  actualStart: z14.string().datetime().nullable().optional(),
  attachments: z14.array(z14.string()).optional()
});
var StartTaskDtoSchema = z14.object({
  actualTravelDuration: z14.number().int().min(0).multipleOf(15)
});
var CompleteTaskDtoSchema = z14.object({
  actualWorkDuration: z14.number().min(0).multipleOf(15),
  actualDepartureDuration: z14.number().min(0).multipleOf(15).nullable(),
  endTime: z14.string().regex(/^\d{2}:\d{2}$/)
});
var ScheduleTaskDtoSchema = z14.object({
  plannedStart: z14.string().datetime(),
  plannedWorkDuration: z14.number().min(0),
  plannedTravelDuration: z14.number().min(0),
  plannedDepartureDuration: z14.number().min(0)
});
var TaskSortFieldSchema = z14.enum([
  "title",
  "plannedStart",
  "deadline",
  "taskType",
  "technicianName",
  "plannedWorkDuration",
  "plannedTravelDuration"
]);
var TaskSortDirectionSchema = z14.enum(["ASC", "DESC"]);
var TechnicianTasksQuerySchema = z14.object({
  from: z14.string(),
  to: z14.string()
});
var TechnicianTasksResponseSchema = z14.object({
  scheduledTasks: z14.array(TaskListItemSchema),
  unscheduledTasks: z14.array(TaskListItemSchema)
});
var TaskFiltersSchema = z14.object({
  page: z14.coerce.number().int().positive().optional(),
  limit: z14.coerce.number().int().positive().max(500).optional(),
  technicianName: z14.string().optional(),
  taskType: TaskTypeSchema.optional(),
  status: TaskStatusSchema.optional(),
  needsRescheduling: z14.coerce.boolean().optional(),
  title: z14.string().optional(),
  plannedStartFrom: z14.string().optional(),
  plannedStartTo: z14.string().optional(),
  deadlineFrom: z14.string().optional(),
  deadlineTo: z14.string().optional(),
  sortField: TaskSortFieldSchema.optional(),
  sortDirection: TaskSortDirectionSchema.optional()
});

// src/reschedule/reschedule.ts
import { z as z15 } from "zod";
var RescheduleItemTypeSchema = z15.enum(["APPOINTMENT", "TASK", "TIME_ENTRY"]);
var RescheduleItemSchema = z15.object({
  id: z15.number().int(),
  type: RescheduleItemTypeSchema,
  title: z15.string(),
  scheduledDate: z15.string().datetime(),
  reschedulingReason: z15.string().nullable().optional(),
  reschedulingRequestedAt: z15.string().nullable().optional(),
  technicianName: z15.string().nullable().optional(),
  entityMeta: z15.union([
    z15.object({ workOrderId: z15.string() }),
    z15.object({ taskType: z15.string() }),
    z15.object({ category: z15.string() })
  ])
});
var RescheduleFiltersSchema = z15.object({
  page: z15.coerce.number().int().positive().optional(),
  limit: z15.coerce.number().int().positive().max(500).optional(),
  type: RescheduleItemTypeSchema.optional(),
  reason: TimeEntryCategorySchema.optional(),
  technicianName: z15.string().optional(),
  dateFrom: z15.string().optional(),
  dateTo: z15.string().optional(),
  title: z15.string().optional(),
  sortField: z15.enum(["scheduledDate", "type", "technicianName"]).optional(),
  sortDirection: z15.enum(["ASC", "DESC"]).optional()
});
var RescheduleCountResponseSchema = z15.object({
  count: z15.number().int()
});

// src/controlling/controlling.ts
import * as zod6 from "zod";
var z17 = zod6.z ?? zod6;
var ControllingFollowUpReasonCodeSchema = z17.enum([
  "MIETER_NICHT_VOR_ORT",
  "TERMIN_NICHT_EINGEHALTEN",
  "WEITERER_GRUND",
  "MIT_ANDEREM_GEWERK",
  "MATERIAL_BESTELLUNG"
]);
var ControllingReasonFilterValueSchema = z17.union([
  ControllingFollowUpReasonCodeSchema,
  TimeEntryCategorySchema
]);
var ControllingSortFieldSchema = z17.enum([
  "repairDate",
  "workOrderId",
  "technician",
  "tenant",
  "ve",
  "building",
  "trade",
  "gewerkId",
  "actualWorkHours",
  "plannedWorkHours",
  "actualTravelHours",
  "plannedTravelHours",
  "actualTotalHours",
  "deviation",
  "deviationHours",
  "wertEuro"
]);
var ScheduleStatusSchema = z17.enum([
  "WITHOUT_FOLLOW_UP",
  "WITH_FOLLOW_UPS",
  "RESCHEDULED_BY_OFFICE",
  "RESCHEDULED_BY_TECHNICIAN"
]);
var DeviationStatusSchema = z17.enum([
  "ON_SCHEDULE",
  "OVER_TARGET",
  "UNDER_TARGET"
]);
var ControllingFiltersSchema = z17.object({
  page: z17.coerce.number().int().positive().optional(),
  limit: z17.coerce.number().int().positive().max(100).optional(),
  sortField: ControllingSortFieldSchema.optional(),
  sortDirection: z17.enum(["ASC", "DESC"]).optional(),
  dateFrom: z17.string().optional(),
  dateTo: z17.string().optional(),
  workOrderId: z17.string().optional(),
  technicianId: z17.coerce.number().int().optional(),
  tenant: z17.string().optional(),
  ve: z17.string().optional(),
  building: z17.string().optional(),
  tradeId: z17.coerce.number().int().optional(),
  technicianTradeId: z17.coerce.number().int().optional(),
  scheduleStatus: z17.array(ScheduleStatusSchema).optional(),
  deviationStatus: z17.array(DeviationStatusSchema).optional(),
  actualTravelHoursMin: z17.coerce.number().optional(),
  actualTravelHoursMax: z17.coerce.number().optional(),
  actualWorkHoursMin: z17.coerce.number().optional(),
  actualWorkHoursMax: z17.coerce.number().optional(),
  actualTotalHoursMin: z17.coerce.number().optional(),
  actualTotalHoursMax: z17.coerce.number().optional(),
  deviationMin: z17.coerce.number().optional(),
  deviationMax: z17.coerce.number().optional(),
  reasons: z17.array(ControllingReasonFilterValueSchema).optional()
});
var ControllingAggregatesSchema = z17.object({
  totalPlannedWorkHours: z17.number(),
  totalActualWorkHours: z17.number(),
  totalPlannedTravelHours: z17.number(),
  totalActualTravelHours: z17.number(),
  totalPlannedHours: z17.number(),
  totalActualHours: z17.number(),
  deviation: z17.number(),
  totalWertEuro: z17.number()
});
var ControllingRowSchema = z17.object({
  workOrderId: z17.string(),
  tenant: z17.string(),
  technicians: z17.array(z17.string()),
  ve: z17.string(),
  building: z17.string(),
  trade: z17.string(),
  scheduleStatus: ScheduleStatusSchema,
  deviationStatus: DeviationStatusSchema,
  actualWorkHours: z17.number(),
  plannedWorkHours: z17.number(),
  actualTravelHours: z17.number(),
  plannedTravelHours: z17.number(),
  actualTotalHours: z17.number(),
  plannedTotalHours: z17.number(),
  repairDateFrom: z17.string(),
  repairDateTo: z17.string(),
  appointmentCount: z17.number(),
  deviation: z17.number(),
  reasons: z17.array(z17.string()),
  technicianTradeIds: z17.array(z17.number().int()),
  gewerkId: z17.number().int().nullable(),
  deviationHours: z17.number(),
  wertEuro: z17.number().nullable(),
  preis: z17.number().nullable()
});
var ControllingPdfColumnSchema = z17.enum([
  "workOrderId",
  "scheduleStatus",
  "repairDate",
  "building",
  "tenant",
  "gewerkId",
  "trade",
  "ve",
  "technician",
  "plannedWorkHours",
  "actualWorkHours",
  "plannedTravelHours",
  "actualTravelHours",
  "plannedTotalHours",
  "actualTotalHours",
  "deviation",
  "deviationHours",
  "reasons",
  "wertEuro"
]);
var ControllingActiveFilterSchema = z17.object({
  label: z17.string(),
  value: z17.string()
});
var ControllingPdfRequestBodySchema = z17.object({
  dateFrom: z17.string().optional(),
  dateTo: z17.string().optional(),
  workOrderId: z17.string().optional(),
  technicianId: z17.coerce.number().int().optional(),
  tenant: z17.string().optional(),
  ve: z17.string().optional(),
  building: z17.string().optional(),
  tradeId: z17.coerce.number().int().optional(),
  technicianTradeId: z17.coerce.number().int().optional(),
  scheduleStatus: z17.array(ScheduleStatusSchema).optional(),
  deviationStatus: z17.array(DeviationStatusSchema).optional(),
  actualTravelHoursMin: z17.coerce.number().optional(),
  actualTravelHoursMax: z17.coerce.number().optional(),
  actualWorkHoursMin: z17.coerce.number().optional(),
  actualWorkHoursMax: z17.coerce.number().optional(),
  actualTotalHoursMin: z17.coerce.number().optional(),
  actualTotalHoursMax: z17.coerce.number().optional(),
  deviationMin: z17.coerce.number().optional(),
  deviationMax: z17.coerce.number().optional(),
  reasons: z17.array(ControllingReasonFilterValueSchema).optional(),
  sortField: ControllingSortFieldSchema.optional(),
  sortDirection: z17.enum(["ASC", "DESC"]).optional(),
  selectedColumns: z17.array(ControllingPdfColumnSchema).min(1),
  activeFilters: z17.array(ControllingActiveFilterSchema)
});
var ControllingXlsxRequestBodySchema = ControllingPdfRequestBodySchema.omit({
  activeFilters: true
});

// src/mail-setting/mail-setting.ts
import * as zod7 from "zod";
var z19 = zod7.z ?? zod7;
var MailSettingSchema = z19.object({
  host: z19.string().min(1),
  port: z19.coerce.number().int().min(1).max(65535),
  user: z19.string(),
  from: z19.string().email()
});
var MailSettingResponseSchema = MailSettingSchema.extend({
  isPassSet: z19.boolean(),
  werkhofleiterEmails: z19.array(z19.string().email()).default([])
});
var UpdateMailSettingDtoSchema = z19.object({
  host: z19.string().min(1).optional(),
  port: z19.coerce.number().int().min(1).max(65535).optional(),
  user: z19.string().optional(),
  pass: z19.string().trim().min(1).optional(),
  from: z19.string().email().optional(),
  werkhofleiterEmails: z19.array(z19.string().email()).optional()
});

// src/arbeitszeit-report/arbeitszeit-report.ts
import * as zod8 from "zod";
var z21 = zod8.z ?? zod8;
var ArbeitszeitGesamtCategorySchema = z21.enum([
  "auftraege",
  "allgemeinzeit",
  "urlaub",
  "krankheit",
  "privat",
  "gesamt"
]);
var ArbeitszeitGesamtRowSchema = z21.object({
  category: ArbeitszeitGesamtCategorySchema,
  sollStd: z21.number().nullable(),
  istStd: z21.number(),
  abweichungStd: z21.number().nullable(),
  abweichungPct: z21.number().nullable(),
  wertEuro: z21.number().nullable()
});
var AuslastungRowSchema = z21.object({
  technicianId: z21.number().int(),
  technicianName: z21.string(),
  sollStd: z21.number(),
  reparaturzeitenStd: z21.number(),
  nichtReparaturzeitenStd: z21.number(),
  istStd: z21.number(),
  abwesenheitenStd: z21.number(),
  auslastungPct: z21.number().nullable()
});
var ArbeitsorgOrganisationTypeSchema = z21.enum([
  "ERRANDS",
  "GENERAL_TASK",
  "SICK_LEAVE",
  "VACATION",
  "PERSONAL_LEAVE",
  "TRAINING"
]);
var ArbeitsorgRowSchema = z21.object({
  id: z21.string(),
  technicianId: z21.number().int().nullable(),
  technicianName: z21.string(),
  organisationType: ArbeitsorgOrganisationTypeSchema,
  bereich: z21.string().nullable(),
  dateFrom: z21.string(),
  dateTo: z21.string(),
  timeFrom: z21.string().nullable(),
  timeTo: z21.string().nullable(),
  istStd: z21.number().nullable(),
  wertEuro: z21.number().nullable()
});
var PaginatedMetaSchema = z21.object({
  page: z21.number().int(),
  limit: z21.number().int(),
  total: z21.number().int()
});
var ArbeitszeitReportFiltersSchema = z21.object({
  dateFrom: z21.string().optional(),
  dateTo: z21.string().optional(),
  technicianId: z21.coerce.number().int().optional(),
  tradeId: z21.coerce.number().int().optional(),
  scheduleStatus: z21.array(ScheduleStatusSchema).optional(),
  deviationStatus: z21.array(DeviationStatusSchema).optional(),
  pageAuftraege: z21.coerce.number().int().positive().optional(),
  pageAuslastung: z21.coerce.number().int().positive().optional(),
  pageArbeitsorganisation: z21.coerce.number().int().positive().optional()
});
var ArbeitszeitReportResponseSchema = z21.object({
  gesamtuebersicht: z21.array(ArbeitszeitGesamtRowSchema),
  auslastung: z21.object({
    items: z21.array(AuslastungRowSchema),
    meta: PaginatedMetaSchema
  }),
  auftraege: z21.object({
    items: z21.array(ControllingRowSchema),
    meta: PaginatedMetaSchema,
    aggregates: ControllingAggregatesSchema
  }),
  arbeitsorganisation: z21.object({
    items: z21.array(ArbeitsorgRowSchema),
    meta: PaginatedMetaSchema
  })
});
export {
  AppointmentSchema,
  AppointmentStatusSchema,
  AppointmentTechnicianSchema,
  ArbeitsorgOrganisationTypeSchema,
  ArbeitsorgRowSchema,
  ArbeitszeitGesamtCategorySchema,
  ArbeitszeitGesamtRowSchema,
  ArbeitszeitReportFiltersSchema,
  ArbeitszeitReportResponseSchema,
  AuditLogActionSchema,
  AuditLogActorSchema,
  AuditLogEntityTypeSchema,
  AuditLogFiltersSchema,
  AuditLogSchema,
  AuslastungRowSchema,
  AuthErrorCode,
  CompleteAppointmentDtoSchema,
  CompleteTaskDtoSchema,
  ControllingActiveFilterSchema,
  ControllingAggregatesSchema,
  ControllingFiltersSchema,
  ControllingFollowUpReasonCodeSchema,
  ControllingPdfColumnSchema,
  ControllingPdfRequestBodySchema,
  ControllingReasonFilterValueSchema,
  ControllingRowSchema,
  ControllingSortFieldSchema,
  ControllingXlsxRequestBodySchema,
  CreateAppointmentDtoSchema,
  CreateTaskDtoSchema,
  CreateTechnicianProfileDtoSchema,
  CreateTimeManagementDtoSchema,
  CreateUserDtoSchema,
  CreateWorkOrderDtoSchema,
  DeviationStatusSchema,
  FollowUpAppointmentDtoSchema,
  GewerkFiltersSchema,
  GewerkSchema,
  MailSettingResponseSchema,
  MailSettingSchema,
  PaginatedMetaSchema,
  ROLE_RANK,
  RescheduleCountResponseSchema,
  RescheduleFiltersSchema,
  RescheduleItemSchema,
  RescheduleItemTypeSchema,
  Role,
  ScheduleStatusSchema,
  ScheduleTaskDtoSchema,
  StartAppointmentDtoSchema,
  StartTaskDtoSchema,
  TaskFiltersSchema,
  TaskListItemSchema,
  TaskSchema,
  TaskSortDirectionSchema,
  TaskSortFieldSchema,
  TaskStatusSchema,
  TaskTechnicianSchema,
  TaskTypeSchema,
  TechnicianAppointmentsItemSchema,
  TechnicianAppointmentsQuerySchema,
  TechnicianAppointmentsResultSchema,
  TechnicianProfileDataSchema,
  TechnicianProfileSchema,
  TechnicianTasksQuerySchema,
  TechnicianTasksResponseSchema,
  TimeEntryCategorySchema,
  TimeEntryConflictItemSchema,
  TimeEntrySchema,
  TimeEntryTechnicianSchema,
  TimeEntryTypeSchema,
  TimeManagementFiltersSchema,
  TimeManagementListItemSchema,
  TimeManagementSortDirectionSchema,
  TimeManagementSortFieldSchema,
  UpdateAppointmentDtoSchema,
  UpdateMailSettingDtoSchema,
  UpdateTaskDtoSchema,
  UpdateTimeManagementDtoSchema,
  UpdateUserDtoSchema,
  UpsertBreakDtoSchema,
  UserFiltersSchema,
  UserRoleSchema,
  UserRolesSchema,
  UserStatusFilterSchema,
  WorkOrderExternalSnapshotSchema,
  WorkOrderFiltersSchema,
  WorkOrderLocalDataSchema,
  WorkOrderMieterSchema,
  WorkOrderSchema,
  WorkOrderSortDirectionSchema,
  WorkOrderSortFieldSchema,
  WorkOrderStatusSchema,
  WorkOrderWithAppointmentsSchema,
  hasAnyRole,
  hasOnlyRole,
  hasRole,
  highestRole
};
